import { useState, useEffect, useRef, useCallback } from "react";
import {
  correctRequest, reprocessRequest, fetchRequest, approveRequest, updateRule, deleteRule,
} from "../api/client";
import type { GeneratedRule, RdiForceMatch, RdiOperatorOverrides } from "../api/client";
import type { CertifyCallerPayload, CorrectionItem, ProcessingRequest, TicketMatch } from "../api/types";
import { stripPkPrefix } from "../helpers/entityUtils";

const STEPS_WITH_CORRECTIONS = [
  "Salvataggio correzioni e generazione regola…",
  "Apprendimento…",
  "Rielaborazione matching…",
];
const STEPS_PLAIN = [
  "Avvio rielaborazione…",
  "In elaborazione…",
  "Verifica risultati…",
];
const POLL_INTERVAL = 2000;
const POLL_TIMEOUT = 60000;

interface ReprocessModalProps {
  requestId: string;
  corrections: CorrectionItem[];
  correctionNote?: string;
  operatorOverrides?: RdiOperatorOverrides;
  /* Certificazione del chiamante decisa nella pagina prima di aprire il
     modale (fase 7): viaggia nello stesso payload dell'approvazione che
     chiude il riprocesso. null/assente ⇒ nessuna certificazione. */
  certifyCaller?: CertifyCallerPayload | null;
  /** Riassunto in chiaro della scelta, per non certificare "di nascosto". */
  certifySummary?: string | null;
  onComplete: (updatedRequest: ProcessingRequest) => void;
  onCancel: () => void;
}

/* ── Helpers ─────────────────────────────────────────────────────────── */

function entityLabel(match: TicketMatch, key: string): string {
  const em = match.entity_matches[key];
  if (!em?.best?.entity) return "—";
  const f = em.best.entity.fields;
  const desc = f.descrizione ?? f.chiamante ?? f.nominativo ?? f.ragsoc ?? f.ragione_sociale ?? f.desc_tipo ?? f.nome;
  return desc ? `${String(desc)} (${stripPkPrefix(em.best.entity.pk)})` : stripPkPrefix(em.best.entity.pk);
}

function entityPk(match: TicketMatch, key: string): string | undefined {
  return match.entity_matches[key]?.best?.entity?.pk;
}

interface CorrectionCheck {
  entityType: string;
  wantedPk: string;
  wantedLabel: string;
  gotPk: string | undefined;
  gotLabel: string;
  prevLabel: string;
  accepted: boolean;
}

function buildCorrectionChecks(
  corrections: CorrectionItem[],
  prev: TicketMatch | undefined,
  curr: TicketMatch | undefined,
): CorrectionCheck[] {
  if (!curr) return [];
  return corrections
    .filter((c) => c.corrected_value && typeof c.corrected_value === "object" && c.corrected_value.pk)
    .flatMap((c) => {
      const key = c.field_corrected;
      const wantedPk = (c.corrected_value as Record<string, unknown>).pk as string;
      const wf = (c.corrected_value as Record<string, unknown>).fields as Record<string, unknown> | undefined;
      const cvLabel = (c.corrected_value as Record<string, unknown>).label as string | undefined;
      const wantedLabel = wf
        ? String(wf.descrizione ?? wf.chiamante ?? wf.nominativo ?? wf.ragsoc ?? wf.ragione_sociale ?? wf.desc_tipo ?? wf.nome ?? cvLabel ?? wantedPk)
        : (cvLabel ?? wantedPk);
      const gotPk = entityPk(curr, key);
      // NB: non scartare più il check quando gotPk è undefined (sistema non
      // ha trovato nulla per questa entità, es. impianto mai auto-rilevato).
      // Prima del fix 2026-07-13 questo caso veniva filtrato via, quindi
      // l'operatore non vedeva mai il conflitto né il bottone "Forza il mio
      // match" — la correzione restava salvata solo come apprendimento
      // futuro (learned association), mai applicata al ticket corrente.
      // gotPk===undefined !== wantedPk → accepted resta correttamente false.
      const gotLabel = entityLabel(curr, key);
      const prevLabel = prev ? entityLabel(prev, key) : "—";
      return [{ entityType: key, wantedPk, wantedLabel, gotPk, gotLabel, prevLabel, accepted: gotPk === wantedPk }];
    });
}

/* ── Main component ──────────────────────────────────────────────────── */

export function ReprocessModal({
  requestId,
  corrections,
  correctionNote,
  operatorOverrides,
  certifyCaller,
  certifySummary,
  onComplete,
  onCancel,
}: ReprocessModalProps) {
  const [step, setStep] = useState(0);
  const [error, setError] = useState<string | null>(null);
  const [done, setDone] = useState(false);
  const [actionLoading, setActionLoading] = useState(false);
  const [updatedRequest, setUpdatedRequest] = useState<ProcessingRequest | null>(null);
  const [rules, setRules] = useState<GeneratedRule[]>([]);
  const [ruleTexts, setRuleTexts] = useState<Record<string, string>>({});
  const [deletedRules, setDeletedRules] = useState<Set<string>>(new Set());
  const cancelled = useRef(false);

  /* Le correzioni da inviare sono quelle **al momento dell'apertura** del
     modale: il componente viene montato di fresco a ogni riprocesso, quindi
     congelarle qui è la semantica giusta.

     Serve perché `corrections` arriva da un `.map()` inline nel genitore: la
     sua identità cambia a **ogni** render di Review3. Con quelle prop nelle
     deps l'effetto ripartiva, il cleanup metteva `cancelled.current = true`
     (senza mai rimetterlo a false) e il risultato era una seconda POST
     `/correct` seguita da un modale piantato sul primo passo per sempre.
     Osservato dal vero il 2026-09-05: basta la derivazione asincrona
     dell'impianto dopo la correzione dell'edificio a innescarlo. */
  const frozen = useRef({ corrections, correctionNote, operatorOverrides });

  // Phase 1: fire corrections + poll — una sola volta per montaggio
  useEffect(() => {
    let interval: ReturnType<typeof setInterval>;
    const { corrections: frozenCorrections, correctionNote: frozenNote,
            operatorOverrides: frozenOverrides } = frozen.current;
    cancelled.current = false;

    async function run() {
      try {
        setStep(0);

        if (frozenCorrections.length > 0) {
          // Caso normale: salva correzioni → il backend triggera il reprocess
          const res = await correctRequest(
            requestId,
            frozenCorrections,
            frozenNote || "Correzione match operatore",
            frozenOverrides,
          );
          if (cancelled.current) return;
          if (res.generated_rules?.length) {
            setRules(res.generated_rules);
            const texts: Record<string, string> = {};
            for (const r of res.generated_rules) texts[r.id] = r.rule_text;
            setRuleTexts(texts);
          }
        } else {
          // Nessuna correzione manuale: riprocessa direttamente (no /correct)
          await reprocessRequest(requestId);
          if (cancelled.current) return;
        }

        setStep(1);
        await new Promise<void>((r) => setTimeout(r, 800));
        if (cancelled.current) return;
        setStep(2);

        const t0 = Date.now();
        interval = setInterval(async () => {
          if (cancelled.current) { clearInterval(interval); return; }
          try {
            const req = await fetchRequest(requestId);
            if (["pending_review", "needs_review", "done"].includes(req.status)) {
              clearInterval(interval);
              setUpdatedRequest(req);
              setDone(true);
            } else if (Date.now() - t0 > POLL_TIMEOUT) {
              clearInterval(interval);
              setError("Timeout — la rielaborazione sta impiegando più del previsto.");
            }
          } catch { /* transient */ }
        }, POLL_INTERVAL);
      } catch (e) {
        setError((e as Error).message);
      }
    }
    run();
    return () => { cancelled.current = true; clearInterval(interval); };
  }, [requestId]);

  // Derive correction checks
  const checks = done && updatedRequest
    ? buildCorrectionChecks(
        corrections,
        updatedRequest.previous_matches?.[0],
        updatedRequest.matches?.[0],
      )
    : [];
  const allAccepted = checks.length > 0 && checks.every((c) => c.accepted);
  const failedChecks = checks.filter((c) => !c.accepted);

  // Persist edited/deleted rules then approve
  const persistRulesAndApprove = useCallback(async (
    forceMatches?: Record<string, RdiForceMatch>,
  ) => {
    setActionLoading(true);
    try {
      // Persist rule edits
      for (const r of rules) {
        if (deletedRules.has(r.id)) {
          await deleteRule(r.id).catch(() => {});
        } else if (ruleTexts[r.id] && ruleTexts[r.id] !== r.rule_text) {
          await updateRule(r.id, { rule_text: ruleTexts[r.id] }).catch(() => {});
        }
      }
      await approveRequest(
        requestId,
        "Approvato dopo rielaborazione",
        operatorOverrides,
        forceMatches,
        certifyCaller,
      );
      // Refresh and close
      const req = await fetchRequest(requestId);
      onComplete(req);
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setActionLoading(false);
    }
  }, [
    requestId,
    rules,
    ruleTexts,
    deletedRules,
    onComplete,
    operatorOverrides,
    certifyCaller,
  ]);

  const handleApprove = () => persistRulesAndApprove();

  const handleForce = () => {
    const fm: Record<string, RdiForceMatch> = {};
    for (const c of failedChecks) {
      const cv = corrections.find((x) => x.field_corrected === c.entityType)?.corrected_value;
      if (cv && typeof cv === "object" && (cv as Record<string, unknown>).pk) {
        fm[c.entityType] = {
          pk: (cv as Record<string, unknown>).pk as string,
          fields: (cv as Record<string, unknown>).fields as Record<string, unknown> | undefined,
        };
      }
    }
    persistRulesAndApprove(fm);
  };

  const handleDeleteRule = (id: string) => setDeletedRules((s) => new Set(s).add(id));
  const handleRestoreRule = (id: string) => setDeletedRules((s) => { const n = new Set(s); n.delete(id); return n; });

  return (
    <div className="dialog-overlay">
      <div className="dialog-card" style={{ maxWidth: 720 }}
           onClick={(e) => e.stopPropagation()}>

        <div className="dialog-header">
          <h2 style={{ display: "flex", alignItems: "center", gap: 8 }}>
            <span className="material-symbols-outlined" style={{ color: "var(--indaco-brand-primary)" }}>refresh</span>
            Rielaborazione
          </h2>
        </div>

        {/* ── Progress steps ── */}
        <div style={{ marginBottom: "var(--space-4)" }}>
          {(corrections.length > 0 ? STEPS_WITH_CORRECTIONS : STEPS_PLAIN).map((label, idx) => {
            const isActive = idx === step && !done && !error;
            const isDone = idx < step || done;
            return (
              <div key={idx} style={{
                display: "flex", alignItems: "center", gap: 8,
                padding: "6px 0", opacity: idx > step && !done ? 0.4 : 1,
              }}>
                {isDone
                  ? <span className="material-symbols-outlined" style={{ fontSize: 20, color: "var(--indaco-success)" }}>check_circle</span>
                  : isActive
                  ? <span className="spinner" style={{ width: 20, height: 20 }} />
                  : <span className="material-symbols-outlined" style={{ fontSize: 20, color: "var(--indaco-gray-300)" }}>radio_button_unchecked</span>}
                <span style={{ fontWeight: isDone ? 500 : isActive ? 600 : 400, color: !isDone && !isActive ? "var(--indaco-gray-400)" : undefined }}>
                  {label}
                </span>
              </div>
            );
          })}
        </div>

        {/* ── Error ── */}
        {error && (
          <div style={{ marginBottom: "var(--space-3)", padding: "var(--space-3)", background: "var(--indaco-danger-light)", borderRadius: "var(--border-radius-lg)", color: "var(--indaco-danger)", fontSize: 13 }}>
            <span className="material-symbols-outlined" style={{ fontSize: 16, marginRight: 4, verticalAlign: "text-bottom" }}>error</span>
            {error}
          </div>
        )}

        {/* ── Generated rules (editable) ── */}
        {rules.length > 0 && (
          <div style={{ marginBottom: "var(--space-4)" }}>
            <div style={{ fontSize: 13, fontWeight: 600, marginBottom: 8, color: "var(--indaco-gray-700)" }}>
              <span className="material-symbols-outlined" style={{ fontSize: 16, verticalAlign: "text-bottom", marginRight: 4 }}>school</span>
              Regola generata
            </div>
            {rules.map((r) => {
              const isDeleted = deletedRules.has(r.id);
              return (
                <div key={r.id} style={{
                  padding: "var(--space-3)", borderRadius: "var(--border-radius-lg)", marginBottom: 6, position: "relative",
                  background: isDeleted ? "var(--indaco-gray-100)" : "var(--indaco-gray-50)",
                  border: isDeleted ? "1px dashed var(--indaco-gray-300)" : "1px solid var(--indaco-gray-200)",
                  opacity: isDeleted ? 0.5 : 1,
                }}>
                  {/* Entity type badge */}
                  <div style={{ fontSize: 11, color: "var(--indaco-brand-primary)", fontWeight: 600, marginBottom: 6, textTransform: "uppercase" }}>
                    {r.entity_type}
                  </div>

                  {isDeleted ? (
                    <div style={{ fontSize: 13, color: "var(--indaco-gray-500)", fontStyle: "italic" }}>
                      Regola eliminata
                      <button className="btn-link" onClick={() => handleRestoreRule(r.id)}
                        style={{ marginLeft: 8, textDecoration: "underline" }}>
                        Ripristina
                      </button>
                    </div>
                  ) : (
                    <>
                      <textarea className="form-textarea"
                        value={ruleTexts[r.id] ?? r.rule_text}
                        onChange={(e) => setRuleTexts((prev) => ({ ...prev, [r.id]: e.target.value }))}
                        rows={3}
                        style={{ fontSize: 13, lineHeight: 1.5 }}
                      />
                      <button className="icon-btn icon-btn-danger" onClick={() => handleDeleteRule(r.id)} title="Elimina regola"
                        style={{ position: "absolute", top: 8, right: 8 }}>
                        <span className="material-symbols-outlined" style={{ fontSize: 18 }}>delete</span>
                      </button>
                      {r.keywords.length > 0 && (
                        <div style={{ marginTop: 6, display: "flex", gap: 4, flexWrap: "wrap" }}>
                          {r.keywords.map((kw, i) => (
                            <span key={i} className="badge badge-neutral">{kw}</span>
                          ))}
                        </div>
                      )}
                    </>
                  )}
                </div>
              );
            })}
          </div>
        )}

        {/* ── Correction checks ── */}
        {done && checks.length > 0 && (
          <div style={{ marginBottom: "var(--space-4)" }}>
            <div style={{ fontSize: 13, fontWeight: 600, marginBottom: 8, color: "var(--indaco-gray-700)" }}>
              <span className="material-symbols-outlined" style={{ fontSize: 16, verticalAlign: "text-bottom", marginRight: 4 }}>fact_check</span>
              Verifica correzioni
            </div>
            {checks.map((c, i) => (
              <div key={i} style={{
                padding: "10px 12px", borderRadius: "var(--border-radius-lg)", marginBottom: 6, fontSize: 13,
                background: c.accepted ? "var(--indaco-success-light)" : "var(--indaco-danger-light)",
                border: c.accepted ? "1px solid var(--indaco-success)" : "1px solid var(--indaco-danger)",
              }}>
                <div style={{ display: "flex", alignItems: "center", gap: 6, marginBottom: 4 }}>
                  <span className="material-symbols-outlined" style={{ fontSize: 18, color: c.accepted ? "var(--indaco-success)" : "var(--indaco-danger)" }}>
                    {c.accepted ? "check_circle" : "cancel"}
                  </span>
                  <span style={{ fontWeight: 600, textTransform: "capitalize" }}>{c.entityType}</span>
                  <span style={{ color: c.accepted ? "var(--indaco-success)" : "var(--indaco-danger)", fontWeight: 500 }}>
                    {c.accepted ? "Correzione recepita" : "Il sistema ha scelto diversamente"}
                  </span>
                </div>
                <div style={{ marginLeft: 24, color: "var(--indaco-gray-600)", lineHeight: 1.6 }}>
                  {c.accepted ? (
                    <>
                      <span style={{ color: "var(--indaco-gray-400)" }}>{c.prevLabel}</span>
                      <span className="material-symbols-outlined" style={{ fontSize: 14, verticalAlign: "middle", margin: "0 4px", color: "var(--indaco-gray-400)" }}>arrow_forward</span>
                      <span style={{ color: "var(--indaco-success)", fontWeight: 600 }}>{c.wantedLabel} ({c.wantedPk})</span>
                    </>
                  ) : (
                    <>
                      <div>
                        <span style={{ color: "var(--indaco-gray-500)" }}>Volevi:</span>{" "}
                        <span style={{ fontWeight: 600 }}>{c.wantedLabel} ({c.wantedPk})</span>
                      </div>
                      <div>
                        <span style={{ color: "var(--indaco-gray-500)" }}>Sistema:</span>{" "}
                        <span style={{ color: "var(--indaco-danger)", fontWeight: 500 }}>{c.gotLabel}</span>
                      </div>
                    </>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}

        {/* ── Match result summary (reprocess senza correzioni manuali) ── */}
        {corrections.length === 0 && done && updatedRequest && (() => {
          const prev = updatedRequest.previous_matches?.[0] ?? null;
          const curr = updatedRequest.matches?.[0] ?? null;
          if (!curr) return null;
          const analyses = updatedRequest.analyses;
          const analysis = (Array.isArray(analyses) ? analyses[0] : analyses) as Record<string, unknown> | null;
          const causale = (analysis?.causale as string | undefined) || "apertura";
          const CAUSALE_LABEL: Record<string, string> = {
            apertura: "Apertura", aggiornamento: "Aggiornamento",
            duplicato_allarme: "Duplicato allarme", escalation_ticket_esistente: "Escalation",
          };
          const confPrev = prev !== null ? Math.round(prev.overall_confidence * 100) : null;
          const confCurr = Math.round(curr.overall_confidence * 100);
          const confColor = confCurr >= 80 ? "var(--indaco-success)" : confCurr >= 50 ? "var(--indaco-warning)" : "var(--indaco-danger)";
          const ENTITIES: Array<{ key: string; label: string }> = [
            { key: "edificio", label: "Edificio" },
            { key: "impianto", label: "Impianto" },
          ];
          return (
            <div style={{ marginBottom: "var(--space-4)" }}>
              <div style={{ fontSize: 13, fontWeight: 600, marginBottom: 8, color: "var(--indaco-gray-700)" }}>
                <span className="material-symbols-outlined" style={{ fontSize: 16, verticalAlign: "text-bottom", marginRight: 4 }}>fact_check</span>
                Risultato rielaborazione
              </div>

              {/* Confidenza + causale */}
              <div style={{ padding: "8px 12px", borderRadius: "var(--border-radius-lg)", marginBottom: 6, fontSize: 13, background: "var(--indaco-gray-50)", border: "1px solid var(--indaco-gray-200)", display: "flex", alignItems: "center", gap: 6 }}>
                <span style={{ fontWeight: 600, color: "var(--indaco-gray-700)", minWidth: 80 }}>Confidenza</span>
                {confPrev !== null && <><span style={{ color: "var(--indaco-gray-500)" }}>{confPrev}%</span><span className="material-symbols-outlined" style={{ fontSize: 14, color: "var(--indaco-gray-400)" }}>arrow_forward</span></>}
                <span style={{ fontWeight: 600, color: confColor }}>{confCurr}%</span>
                <span style={{ marginLeft: "auto", fontSize: 11, background: "var(--indaco-gray-100)", color: "var(--indaco-gray-600)", borderRadius: 4, padding: "2px 6px" }}>
                  {CAUSALE_LABEL[causale] ?? causale}
                </span>
              </div>

              {/* Edificio + impianto */}
              {ENTITIES.map(({ key, label }) => {
                const prevLabel = prev ? entityLabel(prev, key) : null;
                const currLabel = entityLabel(curr, key);
                const prevPk = prev ? entityPk(prev, key) : null;
                const currPk = entityPk(curr, key);
                const changed = prevPk != null && currPk != null && prevPk !== currPk;
                const method = curr.entity_matches[key]?.match_method ?? curr.entity_matches[key]?.best?.method;
                return (
                  <div key={key} style={{
                    padding: "8px 12px", borderRadius: "var(--border-radius-lg)", marginBottom: 6, fontSize: 13,
                    background: changed ? "var(--indaco-success-light)" : "var(--indaco-gray-50)",
                    border: changed ? "1px solid var(--indaco-success)" : "1px solid var(--indaco-gray-200)",
                    display: "flex", alignItems: "center", gap: 6, flexWrap: "wrap",
                  }}>
                    <span className="material-symbols-outlined" style={{ fontSize: 16, color: changed ? "var(--indaco-success)" : "var(--indaco-gray-400)", flexShrink: 0 }}>
                      {changed ? "autorenew" : "check"}
                    </span>
                    <span style={{ fontWeight: 600, color: "var(--indaco-gray-700)", minWidth: 65 }}>{label}</span>
                    {changed && prevLabel && <>
                      <span style={{ color: "var(--indaco-gray-500)", fontSize: 12 }}>{prevLabel}</span>
                      <span className="material-symbols-outlined" style={{ fontSize: 13, color: "var(--indaco-gray-400)" }}>arrow_forward</span>
                    </>}
                    <span style={{ fontWeight: changed ? 600 : 400, color: changed ? "var(--indaco-success)" : "var(--indaco-gray-700)" }}>{currLabel}</span>
                    {method && <span style={{ marginLeft: "auto", fontSize: 11, background: "var(--indaco-gray-100)", color: "var(--indaco-gray-600)", borderRadius: 4, padding: "2px 6px" }}>{method}</span>}
                  </div>
                );
              })}
            </div>
          );
        })()}

        {/* ── Summary message ── */}
        {done && checks.length > 0 && (
          <div style={{
            padding: "10px 12px", borderRadius: "var(--border-radius-lg)", marginBottom: "var(--space-4)", fontSize: 13,
            background: allAccepted ? "var(--indaco-success-light)" : "var(--indaco-warning-light)",
            border: allAccepted ? "1px solid var(--indaco-success)" : "1px solid var(--indaco-warning)",
            display: "flex", alignItems: "center", gap: 8,
          }}>
            <span className="material-symbols-outlined" style={{ fontSize: 20, color: allAccepted ? "var(--indaco-success)" : "var(--indaco-warning)" }}>
              {allAccepted ? "task_alt" : "info"}
            </span>
            <span>
              {allAccepted
                ? "Tutte le correzioni sono state recepite. Puoi inviare alla coda."
                : `${failedChecks.length} correzione/i non recepita/e. Puoi forzare il tuo match o chiudere e ritentare.`}
            </span>
          </div>
        )}

        {/* ── Certificazione del chiamante (fase 7) ──
            Scelta fatta nella pagina prima di aprire il modale; qui va detta
            in chiaro, perché "Invia alla coda" la esegue insieme
            all'approvazione. Si mostra solo se c'è davvero un payload da
            spedire: una scelta incompleta (`selection` a zero edifici) non
            produce payload, quindi qui non compare nulla — non deve poter
            annunciare come restrizione qualcosa che non viene concesso. */}
        {done && certifyCaller && certifySummary && (
          <div style={{
            padding: "10px 12px", borderRadius: "var(--border-radius-lg)", marginBottom: "var(--space-4)", fontSize: 13,
            background: "var(--indaco-info-light)", border: "1px solid var(--indaco-info)",
            display: "flex", alignItems: "center", gap: 8,
          }}>
            <span className="material-symbols-outlined" style={{ fontSize: 20, color: "var(--indaco-info)" }}>
              {certifyCaller.tree?.length ? "verified_user" : "person_off"}
            </span>
            {/* Una scelta che revoca soltanto non si annuncia come una
                certificazione: sarebbe l'opposto di quello che succede. */}
            <span>
              {certifyCaller.tree?.length
                ? <>Con l'invio verrà certificato il chiamante: <b>{certifySummary}</b></>
                : <>Con l'invio verrà <b>revocata</b> la certificazione del chiamante: <b>{certifySummary}</b></>}
            </span>
          </div>
        )}

        {/* ── Action buttons ── */}
        <div style={{ display: "flex", justifyContent: "flex-end", gap: 8, marginTop: 4, flexWrap: "wrap" }}>
          {!done && !error && (
            <button className="btn btn-ghost btn-sm" onClick={onCancel}>
              Annulla
            </button>
          )}
          {error && (
            <button className="btn btn-ghost btn-sm" onClick={onCancel}>
              Chiudi
            </button>
          )}
          {done && (
            <>
              <button className="btn btn-ghost btn-sm" onClick={onCancel} disabled={actionLoading}>
                Chiudi
              </button>
              {failedChecks.length > 0 && (
                <button className="btn btn-warning btn-sm" onClick={handleForce} disabled={actionLoading}>
                  {actionLoading
                    ? <span className="spinner" style={{ width: 14, height: 14 }} />
                    : <span className="material-symbols-outlined" style={{ fontSize: 16 }}>gavel</span>}
                  Forza il mio match
                </button>
              )}
              <button className="btn btn-success btn-sm" onClick={handleApprove} disabled={actionLoading}>
                {actionLoading
                  ? <span className="spinner" style={{ width: 14, height: 14 }} />
                  : <span className="material-symbols-outlined" style={{ fontSize: 16 }}>send</span>}
                Invia alla coda
              </button>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
